import React, { useState, useMemo, useCallback } from "react";
import "./ResidentialComplexList.css";
import IncidentBadge from "../Utils/IncidentBadge";

// иконки
import icLeak from "../../assets/icons/incidents/ic-inc-leak.svg";
import icBatteryLow from "../../assets/icons/incidents/ic-inc-battery-low.svg";
import icTamper from "../../assets/icons/incidents/ic-inc-tamper.svg";
import icInspection from "../../assets/icons/incidents/ic-inc-inspection.svg";
import icBreak from "../../assets/icons/incidents/ic-inc-break.svg";
import icScreenBlock from "../../assets/icons/incidents/ic-inc-screen-block.svg";
import icNoReadings from "../../assets/icons/incidents/ic-inc-no-readings.svg";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useUnit } from "effector-react";
import { $canDeleteComplex, $canEditComplex } from "../../store";
import {
  $deletingIds,
  deleteResidentialComplexFx,
  fetchResidentialComplexesFx,
} from "../../store/complexes";
import { Chip, CircularProgress, IconButton, Tooltip } from "@mui/material";
import ConfirmDialog from "../ConfirmModal";
import EditResidentialComplexDialog from "./EditResidentialComplexDialog";
import { complexKindLabel, isSettlementComplex } from "../../utils/complexKind";

const ICONS = {
  leak: icLeak,
  lowBattery: icBatteryLow,
  tamper: icTamper,
  inspection: icInspection,
  break: icBreak,
  screenBlock: icScreenBlock,
  noReadings: icNoReadings,
};

function stopAll(e) {
  e.preventDefault?.();
  e.stopPropagation?.();
}

export default function ResidentialComplexCard({
  id,
  name,
  address = "",

  addresses = [],
  counters = { cold: 0, hot: 0, heat: 0, heatAllocators: 0, electricity: 0, all: 0 },
  incidents = {}, // { leak, lowBattery, tamper, inspection, break }
  complexKind = "mdu",
  checked = false,
  onCheck,
  collapsedCount = 4, // сколько адресов показывать до "раскрыть"
  onClick,
}) {
  const settlement = isSettlementComplex(complexKind);
  const kindLabel = complexKindLabel(complexKind);
  const [open, setOpen] = useState(false);
  const [canDelete, canEdit, deletingIds, deleteResidentialComplex] = useUnit([
    $canDeleteComplex,
    $canEditComplex,
    $deletingIds,
    deleteResidentialComplexFx,
  ]);
  const deleting = deletingIds.has(String(id));
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [forceConfirmOpen, setForceConfirmOpen] = useState(false);
  const [deviceCount, setDeviceCount] = useState(null);
  const [deletePending, setDeletePending] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const handleDelete = useCallback(
    async (force) => {
      if (!canDelete) return;
      setDeletePending(true);
      try {
        await deleteResidentialComplex({ id: String(id), force });
      } catch (err) {
        const status = err?.response?.status ?? err?.status;
        const detail = err?.response?.data?.detail ?? err?.data?.detail;
        const count = detail?.device_count ?? err?.response?.data?.device_count;
        if (status === 409 && typeof count === "number") {
          setDeviceCount(count);
          setForceConfirmOpen(true);
          return;
        }
        console.error("delete complex failed", err);
      } finally {
        setDeletePending(false);
      }
    },
    [canDelete, deleteResidentialComplex, id]
  );

  const handleCardClick = useCallback(
    (event) => {
      if (!onClick) return;
      if (editOpen || confirmOpen || forceConfirmOpen) {
        event?.preventDefault?.();
        return;
      }
      onClick();
    },
    [onClick, editOpen, confirmOpen, forceConfirmOpen]
  );

  const handleKey = (e) => {
    if (!onClick || editOpen || confirmOpen || forceConfirmOpen) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };

  const shown = useMemo(() => {
    if (open) return addresses;
    return addresses.slice(0, collapsedCount);
  }, [open, addresses, collapsedCount]);

  const leftHasMore = addresses.length > shown.length;

  return (
    <div
      className={`rc-card ${onClick ? "is-clickable" : ""} ${
        deleting ? "rc-card--deleting" : ""
      }`}
      onClick={handleCardClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={handleKey}
      aria-label={name ? `Открыть ${name}` : "Открыть"}
    >
      {/* чекбокс справа */}
      {/* <label className={`rc-check ${checked ? 'is-checked' : ''}`}>
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => onCheck?.(e.target.checked)}
                    aria-label={checked ? 'Убрать из выбора' : 'Выбрать проект'}
                /> */}
      {/* 16×16 зона внутри 48×48, рисуем галочку SVG */}
      {/* <svg className="rc-check__svg" viewBox="0 0 20 20" aria-hidden="true">
                    <polyline points="4,11 8,15 16,6"
                        fill="none" stroke="currentColor" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </label> */}
      {canEdit && (
        <Tooltip title="Обновить жилой комплекс">
          <span
            style={{
              position: "absolute",
              top: 8,
              right: canDelete ? 44 : 8,
              display: "inline-flex",
              pointerEvents: "auto",
              zIndex: 3,
            }}
            onClick={stopAll}
            onMouseDown={stopAll}
            onTouchStart={stopAll}
          >
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => {
                stopAll(e);
                setEditOpen(true);
              }}
              onMouseDown={stopAll}
              onTouchStart={stopAll}
              aria-label="Обновить"
              sx={{
                bgcolor: (theme) =>
                  theme.palette.mode === "light"
                    ? "rgba(0,0,0,0.04)"
                    : "rgba(255,255,255,0.06)",
                "&:hover": {
                  bgcolor: (theme) =>
                    theme.palette.mode === "light"
                      ? "rgba(0,0,0,0.08)"
                      : "rgba(255,255,255,0.12)",
                },
              }}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {canDelete && (
        <Tooltip title="Удалить жилой комплекс">
          <span
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              display: "inline-flex",
              pointerEvents: "auto",
              zIndex: 3,
            }}
            onClick={stopAll}
            onMouseDown={stopAll}
            onTouchStart={stopAll}
          >
            <IconButton
              size="small"
              color="error"
              disabled={deleting || deletePending}
              onClick={(e) => {
                stopAll(e);
                setConfirmOpen(true);
              }}
              onMouseDown={stopAll}
              onTouchStart={stopAll}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") stopAll(e);
              }}
              sx={{
                bgcolor: (theme) =>
                  theme.palette.mode === "light"
                    ? "rgba(0,0,0,0.04)"
                    : "rgba(255,255,255,0.06)",
                "&:hover": {
                  bgcolor: (theme) =>
                    theme.palette.mode === "light"
                      ? "rgba(0,0,0,0.08)"
                      : "rgba(255,255,255,0.12)",
                },
              }}
              aria-label="Удалить"
            >
              {deleting || deletePending ? (
                <CircularProgress size={18} />
              ) : (
                <DeleteOutlineRoundedIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}
      <div className="rc-card__inner rc-card__inner--3cols">
        {/* Левая колонка — адреса */}
        <div className="rc-col rc-col--addresses">
          <div className="rc-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span>{settlement ? 'Дома посёлка' : 'Список домов'}</span>
            <Chip
              size="small"
              label={kindLabel}
              color={settlement ? 'success' : 'default'}
              variant="outlined"
              onClick={stopAll}
              onMouseDown={stopAll}
            />
          </div>
          {addresses.length > 0 ? (
            <>
              <ul className="rc-address-list">
                {shown.map((addr, i) => (
                  <li key={i} className="rc-address-item" title={addr}>
                    {i+1}.{addr}
                  </li>
                ))}
              </ul>

              {leftHasMore && (
                <button
                  type="button"
                  className={`rc-toggle ${open ? "is-open" : ""}`}
                  onClick={(e) => {
                    stopAll(e);
                    setOpen((v) => !v);
                  }}
                  aria-expanded={open}
                  disabled={!leftHasMore && !open}
                  title={open ? "Свернуть" : "Показать ещё"}
                >
                  <span className="rc-chevron" />
                </button>
              )}
            </>
          ) : (
            <div
              className={`rc-empty ${onClick ? "rc-empty--clickable" : ""}`}
              role={onClick ? "button" : undefined}
              tabIndex={onClick ? 0 : undefined}
              onClick={onClick}
              onKeyDown={(e) => {
                if (!onClick) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onClick();
                }
              }}
              aria-label="В этом комплексе пока нет домов. Нажмите, чтобы добавить."
            >
              <span className="rc-empty__icon" aria-hidden="true">
                {/* Домик с плюсом, 24×24 */}
                <svg viewBox="0 0 24 24" width="24" height="24">
                  <path
                    d="M3 11.5l9-7 9 7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M5 10.5v8.5a1.5 1.5 0 001.5 1.5H17.5A1.5 1.5 0 0019 19v-8.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 13v6M9 16h6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              <div className="rc-empty__text">
                <div className="rc-empty__title">Домов пока нет</div>
                <div className="rc-empty__subtitle">Добавьте первый дом</div>
              </div>
              {onClick && (
                <button
                  type="button"
                  className="rc-empty__action"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClick();
                  }}
                >
                  Добавить
                </button>
              )}
            </div>
          )}
        </div>

        {/* Средняя — счётчики */}
        <div className="rc-col rc-col--meters">
          <div className="rc-section-title">Количество счётчиков</div>
        <div className="rc-meters rc-meters--stack">
          <div className="rc-meter">
            <div className="rc-meter__type">ХВС</div>
            <div className="rc-meter__value">{counters.cold ?? 0}</div>
          </div>
          <div className="rc-meter">
            <div className="rc-meter__type">ГВС</div>
            <div className="rc-meter__value">{counters.hot ?? 0}</div>
          </div>
          <div className="rc-meter">
            <div className="rc-meter__type">Отопление</div>
            <div className="rc-meter__value">{counters.heat ?? 0}</div>
          </div>
          <div className="rc-meter">
            <div className="rc-meter__type">УРТ</div>
            <div className="rc-meter__value">{counters.heatAllocators ?? 0}</div>
          </div>
          <div className="rc-meter">
            <div className="rc-meter__type">Электросчетчики</div>
            <div className="rc-meter__value">{counters.electricity ?? 0}</div>
          </div>
          <div className="rc-meter">
            <div className="rc-meter__type">Всего</div>
            <div className="rc-meter__value">{counters.all ?? 0}</div>
          </div>
        </div>
        </div>

        {/* Правая — инциденты (с бейджами) */}
        <div className="rc-col rc-col--incidents">
          <div className="rc-section-title">Инциденты в доме</div>

          <div className="rc-incidents rc-incidents--two">
            {/* левая подколонка */}
            <div className="rc-inc-col">
              {[
                { key: "leak", label: "Протечки", alertIf: (n) => n > 0 },
                { key: "lowBattery", label: "Низкий заряд" },
                { key: "tamper", label: "Воздействие магнитом" },
                { key: "inspection", label: "Поверка" },
                { key: "break", label: "Прорыв" },
              ].map(({ key, label, alertIf }) => (
                <div key={key} className="rc-inc-row">
                  <span className="rc-inc-label">{label}</span>
                  <IncidentBadge
                    icon={ICONS[key]}
                    count={incidents[key] ?? 0}
                    alert={alertIf ? alertIf(incidents[key] ?? 0) : false}
                    title={label}
                  />
                </div>
              ))}
            </div>

            {/* правая подколонка */}
            <div className="rc-inc-col rc-inc-col_right">
              {[
                { key: "screenBlock", label: "Блок. экрана" },
                {
                  key: "noReadings",
                  label: "Отсутствие показаний",
                  alertIf: (n) => n > 0,
                },
              ].map(({ key, label, alertIf }) => (
                <div key={key} className="rc-inc-row">
                  <span className="rc-inc-label">{label}</span>
                  <IncidentBadge
                    icon={ICONS[key]}
                    count={incidents[key] ?? 0}
                    alert={alertIf ? alertIf(incidents[key] ?? 0) : false}
                    title={label}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Название ЖК снизу-справа */}
      {name && <div className="rc-card__title">{name}</div>}
      <EditResidentialComplexDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        complex={{ id, name, address }}
        onSaved={async () => {
          setEditOpen(false);
          try {
            await fetchResidentialComplexesFx();
          } catch (err) {
            // ignore refresh errors
          }
        }}
      />
      <ConfirmDialog
        open={confirmOpen}
        title="Удаление комплекса"
        description={`Удалить объект «${name || id}»? Это действие необратимо.`}
        confirmText="Удалить"
        cancelText="Отмена"
        confirmColor="error"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={async () => {
          setConfirmOpen(false);
          await handleDelete(false);
        }}
      />
      <ConfirmDialog
        open={forceConfirmOpen}
        title="Удалить вместе с устройствами?"
        description={`В комплексе найдено ${deviceCount ?? 0} устройств. Удалить их вместе с комплексом?`}
        confirmText="Удалить вместе"
        cancelText="Отмена"
        confirmColor="error"
        onCancel={() => setForceConfirmOpen(false)}
        onConfirm={async () => {
          setForceConfirmOpen(false);
          await handleDelete(true);
        }}
      />
    </div>
  );
}


